import React, { useState } from "react";
import { Box, Button, Slider, Snackbar, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { selectGameId, selectGameObject } from "features/gamestate/gameStateSlice";
import { QUESTION_TIME_SECONDS } from "features/gamestate/constants";
import ResetGameButton from "../components/ResetGameButton";
import CopyGameButton from "../components/CopyGameButton";
import GameLifecycleActions from "./GameLifecycleActions";
import DangerZone from "../components/DangerZone";
import logging from "utils/logging";
import Provider from "provider";

const log = logging.getLogger("Game Settings"); // eslint-disable-line no-unused-vars

const Root = styled('div')(({ theme }) => ({
  margin: theme.spacing(1),
}));

const SectionHeader = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));

const SliderRow = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  maxWidth: 400,
}));

const TIMER_MIN = 10;
const TIMER_MAX = 60;
const TIMER_STEP = 5;

const GameSettings = () => {
  const gameId = useSelector(selectGameId);
  const gameObject = useSelector(selectGameObject);
  const savedTimers = gameObject?.settings?.timers || {};

  const [regularTime, setRegularTime] = useState(savedTimers.regularQuestion ?? QUESTION_TIME_SECONDS);
  const [dailyDoubleTime, setDailyDoubleTime] = useState(savedTimers.dailyDouble ?? QUESTION_TIME_SECONDS);
  const [saved, setSaved] = useState(false);

  const handleSave = async () => {
    await Provider.updateGameSettings(gameId, {
      timers: {
        regularQuestion: regularTime,
        dailyDouble: dailyDoubleTime,
      },
    });
    setSaved(true);
  };

  return (
    <Root>
      <SectionHeader variant="h4">
        Timer Settings
      </SectionHeader>

      <SliderRow>
        <Typography gutterBottom>
          Regular Questions: <strong>{regularTime}s</strong>
        </Typography>
        <Slider
          value={regularTime}
          onChange={(_, v) => setRegularTime(v)}
          min={TIMER_MIN}
          max={TIMER_MAX}
          step={TIMER_STEP}
          marks
          valueLabelDisplay="auto"
          valueLabelFormat={(v) => `${v}s`}
        />
      </SliderRow>

      <SliderRow>
        <Typography gutterBottom>
          Daily Doubles: <strong>{dailyDoubleTime}s</strong>
        </Typography>
        <Slider
          value={dailyDoubleTime}
          onChange={(_, v) => setDailyDoubleTime(v)}
          min={TIMER_MIN}
          max={TIMER_MAX}
          step={TIMER_STEP}
          marks
          valueLabelDisplay="auto"
          valueLabelFormat={(v) => `${v}s`}
        />
      </SliderRow>

      <Box sx={{ mb: 4 }}>
        <Button variant="contained" onClick={handleSave}>
          Save Timer Settings
        </Button>
      </Box>

      <SectionHeader variant="h4">
        Actions
      </SectionHeader>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center", mb: 2 }}>
        <ResetGameButton />
        <CopyGameButton />
        <GameLifecycleActions />
      </Box>

      <DangerZone />

      <Snackbar
        open={saved}
        autoHideDuration={3000}
        onClose={() => setSaved(false)}
        message="Timer settings saved"
      />
    </Root>
  );
};

export default GameSettings;
